import React, { useState, useContext, useEffect } from "react";
import ReusableModal from "../ManageSettings/ReusableModal";
import { toast } from "react-toastify";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import ChipList from "../../../common/ReusableComponent/ChipList/ChipList";

const AddVendorModal = ({ 
  show, 
  handleClose, 
  updateVendors, 
  serviceData = [], 
  onServiceDataUpdate, 
  onVendorAdded 
}) => {
  const [vendorName, setVendorName] = useState("");
  const [vendorType, setVendorType] = useState("");
  const [vendorDepartments, setVendorDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [openingBalance, setOpeningBalance] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [vendors, setVendors] = useState([]);
  const [serviceDatas, setServiceData] = useState([]); 
  const { user } = useContext(AuthContext);
  const storeId = user.store;
  const { Post, Get, _Get , GetPaginatedData } = useApi();

const handleVendorNameChange = (e) => {
  const value = e.target.value;
  const filtered = value.replace(/[^A-Za-z\s]/g, "");
  const capitalized = filtered.charAt(0).toUpperCase() + filtered.slice(1);
  setVendorName(capitalized);
};

const validateEmail = (email) => {
  const emailRegex =  /^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const handleEmailChange = (e) => {
  const value = e.target.value;
  setEmail(value);
  if (value && !validateEmail(value)) {
    setEmailError("Please enter a valid email address");
  } else {
    setEmailError("");
  }
};

  const handleDepartmentSelect = (selected) => {
    if (selected && !vendorDepartments.some(d => d.id === selected.id)) {
      const title = selected.all_department_detail?.title || selected.all_expense_type_detail?.title || "";
      if (!title) {
        console.error("No title found for selected item:", selected);
        toast.error("Unable to add item - no title found");
        return;
      }
      const newDept = { id: selected.id, title: title };
      setVendorDepartments((prev) => [...prev, newDept]);
      setSelectedDepartments((prev) => [...prev, newDept]);
    }
  };

  const handleChipRemove = (item) => {
    setVendorDepartments((prev) => prev.filter((dept) => dept.id !== item.id));
    setSelectedDepartments((prev) => prev.filter((dept) => dept.id !== item.id));
  };

  const fetchServices = async ({ page = 1, searchTerm = "" } = {}) => {
    try {
      const serviceResponse = await GetPaginatedData("vendorDepartmentServiceData", {
        page,
        search: searchTerm || undefined,
        queryParams: { store: storeId }
      });
      
      const results = serviceResponse?.data || [];
      const formattedData = results.map(item => ({
        id: item.id,
        type: item.all_department_detail ? "department" : "expense type",
        title: item.all_department_detail?.title || item.all_expense_type_detail?.title,
        description: item.all_department_detail?.description || item.all_expense_type_detail?.description
      }));
      
      return { data: formattedData, totalCount: serviceResponse?.totalCount || 0 };
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Error fetching services!");
      return { data: [], totalCount: 0 };
    }
  };



  const handleSave = async (e) => {
    e.preventDefault();

    if (!vendorName.trim()) {
      toast.error("Please enter vendor name!");
      return;
    }

    if (!vendorType) {
      toast.error("Please select vendor type!");
      return;
    }

    if (email && !validateEmail(email)) {
      toast.error("Please enter a valid email address!");
      return;
    }

    if ((vendorType === "purchase" || vendorType === "expense") && vendorDepartments.length === 0) {
      const departmentLabel = vendorType === "purchase" ? "department" : "expense type";
      toast.error(`Please select at least one ${departmentLabel}!`);
      return;
    }

    const payload = {
      name: vendorName,
      type: vendorType,
      opening_balance: openingBalance || "0", 
      vendor_department_service: vendorDepartments.map(d => d.id),
      email: email,
      store: storeId,
    };

    try {
      const response = await Post("vendorDepartmentData", payload);

      if (updateVendors) {
        updateVendors(response.data);
      }
      if (onVendorAdded) {
        onVendorAdded(response.data);
      }

      toast.success("Vendor added successfully!");
      handleClose();
      clearVendorAdd(); 
    } catch (error) {
      console.error("Error saving vendor:", error);
      toast.error("Error saving vendor!");
    }
  };

  const clearVendorAdd = () => { 
    setVendorName("");
    setVendorType("");
    setVendorDepartments([]);
    setSelectedDepartments([]);
    setOpeningBalance("");
    setEmail("");
    setEmailError("");
  };

  const handleExpenseTypeAdded = (newExpenseType) => {
    if (onServiceDataUpdate) {
      onServiceDataUpdate((prev) => [...prev, newExpenseType]); 
    }
  };



  return (
    <ReusableModal
      show={show}
      handleClose={() => {
        handleClose();
        clearVendorAdd(); 
      }}
      title="Add Vendor"
      width="auto"
    >
      <form onSubmit={handleSave}>
        <div
          className="d-flex flex-column"
          style={{
            marginLeft: "20px",
            marginRight: "10px",
            marginTop: "20px",
          }}
        >
          <div className="d-flex align-items-center mb-4 mx-3">
            <div
              className="position-relative"
              style={{ width: "300px", marginRight: "10px" }}
            >
              <input
                id="expenseVendorName"
                type="text"
                className="form-control-setting"
                placeholder=""
                value={vendorName}
                onChange={handleVendorNameChange}
                required
              />
              <label className="floating-label">Vendor Name</label>
            </div>
            <div
              className="position-relative"
              style={{ width: "300px", marginRight: "10px" }}
            >
              <select 
                id="vendorTypeSelect"
                className="form-control-setting"
                value={vendorType}
                onChange={(e) => {
                  setVendorType(e.target.value);
                  setVendorDepartments([]);
                  setOpeningBalance("");
                }}
                required
              >
                <option value="">Select Vendor Type</option>
                <option value="purchase">Purchase</option>
                <option value="expense">Expense</option>
              </select>
              <label className="floating-label">Vendor Type</label>
            </div>
          </div>

         

          {(vendorType === "purchase" || vendorType === "expense") && (
            <div>
              <div className="d-flex mb-4 mx-3">
                <div style={{ width: "300px", marginRight: "10px" }}>

                  <ChipList
                    items={vendorDepartments}
                    onRemove={handleChipRemove}
                    displayField="title"
                    keyField="id"
                    style={{ marginBottom: "10px" }}
                  />

                  <PaginatedSearchSelect
                    api="vendorDepartmentServiceData"
                    GetPaginatedData={GetPaginatedData}
                    getOptionLabel={(opt) => opt.all_department_detail?.title || opt.all_expense_type_detail?.title || ""}
                    onSelect={handleDepartmentSelect}
                    placeholder={vendorType === "purchase" ? "Select Vendor Department" : "Select Expense Type"}
                    filterFn={(item) => {
                      const itemType = item.all_department_detail ? "department" : "expense type";
                      const expectedType = vendorType === "purchase" ? "department" : "expense type";
                      return itemType === expectedType && !vendorDepartments.some(d => d.id === item.id);
                    }}
                  />

                </div>

                {vendorType === "purchase" && (
                  <div className="position-relative" style={{ width: "300px" }}>
                    <input
                      type="number"
                      className="form-control-setting"
                      placeholder=""
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(e.target.value)}
                      min="0"
                      step="0.01"
                    />
                    <label className="floating-label">Opening Balance</label>
                  </div>
                )}
              </div>
            </div>
          )}
 <div className="d-flex align-items-center mb-4 mx-3">
            <div
              className="position-relative"
              style={{ width: "300px", marginRight: "10px" }}
            >
              <input
                id="vendorEmail"
                type="email"
                className={`form-control-setting ${emailError ? 'border-danger' : ''}`}
                placeholder=""
                value={email}
                onChange={handleEmailChange}
              />
              <label className="floating-label">Email (Optional)</label>
              {emailError && (
                <div className="text-danger" style={{ fontSize: "12px", marginTop: "2px" }}>
                  {emailError}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>

            <button
              id="vendorSubmitBtn"
              type="submit"
              style={{
                padding: "4px 8px",
                backgroundColor: "#282e26",
                color: "white",
                border: "none",
                borderRadius: "40px",
                cursor: "pointer",
                width: "150px",
                height: "38px",
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              Save
            </button>
          </div>
        </div>
      </form>
    </ReusableModal>
  );
};

export default AddVendorModal;